import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';

const COUNTRIES = ['CH', 'DE', 'AT'];
const ROLES = ['imam', 'administrator', 'coordinator', 'other'];

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    mosqueName: '', countryCode: 'CH', street: '', houseNumber: '', plz: '', city: '', website: '',
    contactName: '', contactRole: 'administrator', contactEmail: '', contactPhone: '',
    loginEmail: '', password: '', confirmPassword: '', acceptTerms: false, referralCode: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handle = (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [e.target.name]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');

    // Client-side validation with clear messages
    if (!form.mosqueName.trim() || form.mosqueName.trim().length < 3)
      return setError('Name der Moschee muss mindestens 3 Zeichen haben.');
    if (!form.street.trim())
      return setError('Bitte Strasse eingeben.');
    if (!form.houseNumber.trim())
      return setError('Bitte Hausnummer eingeben.');
    if (!form.plz.trim())
      return setError('Bitte PLZ eingeben.');
    if (!form.city.trim() || form.city.trim().length < 2)
      return setError('Bitte Stadt eingeben.');
    if (!form.contactName.trim() || form.contactName.trim().length < 3)
      return setError('Name der Kontaktperson muss mindestens 3 Zeichen haben.');
    if (!form.contactEmail.trim())
      return setError('Bitte Kontakt-E-Mail eingeben.');
    if (!form.loginEmail.trim())
      return setError('Bitte Anmelde-E-Mail eingeben.');
    if (form.password.length < 8)
      return setError('Passwort muss mindestens 8 Zeichen haben.');
    if (!/[a-z]/.test(form.password) || !/[A-Z]/.test(form.password) || !/\d/.test(form.password))
      return setError('Passwort muss Groß- und Kleinbuchstaben sowie mindestens eine Zahl enthalten.');
    if (form.password !== form.confirmPassword)
      return setError('Passwörter stimmen nicht überein.');
    if (!form.acceptTerms)
      return setError('Bitte akzeptieren Sie die Nutzungsbedingungen um fortzufahren.');

    setLoading(true);
    try {
      await api.post('/auth/mosque/register', {
        ...form,
        address: `${form.street} ${form.houseNumber}`.trim(),
        acceptTerms: 'true',
      });
      setSuccess(true);
    } catch (err) {
      const data = err.response?.data;
      if (data?.error === 'Email already registered') {
        setError('Diese E-Mail-Adresse ist bereits registriert.');
      } else if (data?.details?.length) {
        const fieldMap = {
          password: 'Passwort muss mind. 8 Zeichen, Groß- und Kleinbuchstaben sowie eine Zahl enthalten.',
          loginEmail: 'Bitte eine gültige Anmelde-E-Mail eingeben.',
          contactEmail: 'Kontakt-E-Mail ist ungültig.',
          mosqueName: 'Name der Moschee muss mind. 3 Zeichen haben.',
          contactName: 'Name der Kontaktperson muss mind. 3 Zeichen haben.',
          city: 'Bitte eine gültige Stadt eingeben.',
        };
        setError(data.details.map(d => fieldMap[d.field] || d.message).join(' '));
      } else {
        setError(data?.error || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: 20 }}>
        <div className="card" style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
          </div>
          <h2 style={{ color: 'var(--primary)', marginBottom: 12 }}>{t('register.successTitle')}</h2>
          <p style={{ color: 'var(--text2)' }}>{t('register.successMessage')}</p>
          <Link to="/login" className="btn btn-primary mt-6">Zur Anmeldung</Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh', padding: '32px 20px' }}>
      <div style={{ maxWidth: 600, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <h1 style={{ fontFamily: 'Playfair Display', fontSize: 28, color: 'var(--primary-dk)' }}>Ilmi<span style={{ color: 'var(--accent)' }}>fy</span></h1>
          <h2 style={{ fontSize: 20, color: 'var(--text)', marginTop: 8 }}>{t('register.title')}</h2>
        </div>

        <div className="card">
          {error && <div className="alert alert-error">{error}</div>}

          <form onSubmit={submit}>
            {/* Moschee Info */}
            <h3 style={{ fontSize: 16, color: 'var(--text2)', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
              Informationen zur Moschee
            </h3>

            <div className="form-group">
              <label className="form-label">{t('register.mosqueName')} *</label>
              <input className="form-input" name="mosqueName" value={form.mosqueName} onChange={handle} required />
            </div>

            <div className="form-group">
              <label className="form-label">{t('register.country')} *</label>
              <select className="form-select" name="countryCode" value={form.countryCode} onChange={handle}>
                {COUNTRIES.map((c) => <option key={c} value={c}>{t(`countries.${c}`)}</option>)}
              </select>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Strasse *</label>
                <input className="form-input" name="street" value={form.street} onChange={handle} placeholder="Musterstrasse" required />
              </div>
              <div className="form-group">
                <label className="form-label">Hausnummer *</label>
                <input className="form-input" name="houseNumber" value={form.houseNumber} onChange={handle} placeholder="12" required />
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">PLZ *</label>
                <input className="form-input" name="plz" value={form.plz} onChange={handle} placeholder="8001" required maxLength={10} />
              </div>
              <div className="form-group">
                <label className="form-label">Stadt *</label>
                <input className="form-input" name="city" value={form.city} onChange={handle} placeholder="Zürich" required />
              </div>
            </div>

            {/* Kontaktperson */}
            <h3 style={{ fontSize: 16, color: 'var(--text2)', margin: '20px 0 16px', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
              {t('register.contactPerson')}
            </h3>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t('register.contactName')} *</label>
                <input className="form-input" name="contactName" value={form.contactName} onChange={handle} required />
              </div>
              <div className="form-group">
                <label className="form-label">{t('register.contactRole')} *</label>
                <select className="form-select" name="contactRole" value={form.contactRole} onChange={handle}>
                  {ROLES.map((r) => <option key={r} value={r}>{t(`register.roles.${r}`)}</option>)}
                </select>
              </div>
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t('register.contactEmail')} *</label>
                <input className="form-input" type="email" name="contactEmail" value={form.contactEmail} onChange={handle} required />
              </div>
              <div className="form-group">
                <label className="form-label">Telefon</label>
                <input className="form-input" name="contactPhone" value={form.contactPhone} onChange={handle} />
              </div>
            </div>

            {/* Zugangsdaten */}
            <h3 style={{ fontSize: 16, color: 'var(--text2)', margin: '20px 0 16px', paddingBottom: 8, borderBottom: '1px solid var(--border)' }}>
              {t('register.accountSection')}
            </h3>

            <div className="form-group">
              <label className="form-label">{t('register.loginEmail')} *</label>
              <input className="form-input" type="email" name="loginEmail" value={form.loginEmail} onChange={handle} required />
            </div>

            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">{t('auth.password')} *</label>
                <input className="form-input" type="password" name="password" value={form.password} onChange={handle} />
                <div className="form-error" style={{ color: 'var(--text3)' }}>Mind. 8 Zeichen, Groß/Kleinbuchstaben & Zahl</div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('auth.confirmPassword')} *</label>
                <input className="form-input" type="password" name="confirmPassword" value={form.confirmPassword} onChange={handle} required />
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 8 }}>
              <label className="form-label" style={{ color: 'var(--text3)' }}>Empfehlungscode <span style={{ fontWeight: 400, fontSize: 11 }}>(optional)</span></label>
              <input className="form-input" name="referralCode" value={form.referralCode}
                onChange={e => setForm(p => ({ ...p, referralCode: e.target.value.toUpperCase() }))}
                placeholder="z.B. A1B2C3D4" maxLength={20}
                style={{ letterSpacing: 2, fontFamily: 'monospace' }} />
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 3 }}>Falls Sie von einer anderen Moschee empfohlen wurden</div>
            </div>

            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, margin: '16px 0 20px',
              padding: '12px 14px', borderRadius: 8,
              background: form.acceptTerms ? 'var(--success-lt, #ECFDF5)' : 'var(--bg)',
              border: `1px solid ${form.acceptTerms ? '#6EE7B7' : 'var(--border)'}`,
              transition: 'all 0.2s',
            }}>
              <input
                type="checkbox" id="terms" name="acceptTerms"
                checked={form.acceptTerms} onChange={handle}
                style={{ marginTop: 2, width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
              />
              <label htmlFor="terms" style={{ fontSize: 14, color: 'var(--text2)', cursor: 'pointer', lineHeight: 1.5 }}>
                {i18n.language === 'al' ? (
                  <>Pranoj{' '}
                    <a href="https://www.ilmify.ch/agb.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>Kushtet e Përgjithshme (KPP)</a>
                    {' '}dhe{' '}
                    <a href="https://www.ilmify.ch/datenschutz.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>Deklaratën e Privatësisë</a>
                  </>
                ) : (
                  <>Ich akzeptiere die{' '}
                    <a href="https://www.ilmify.ch/agb.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>AGB</a>
                    {' '}und die{' '}
                    <a href="https://www.ilmify.ch/datenschutz.html" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary)', textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>Datenschutzerklärung</a>
                  </>
                )}
              </label>
            </div>

            <button className="btn btn-primary btn-lg w-full" type="submit" disabled={loading}>
              {loading ? t('common.loading') : t('register.submit')}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
            <Link to="/login" style={{ color: 'var(--text3)' }}>Zurück zur Anmeldung</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
